<template>
  <div class="grid-container text-base">
    <!-- Header -->
    <section-header variants="dividing" size="xl" class="md:block lg:flex">
      <template slot="content">
        <heading level="1" size="xl" qaid="allocated-backorder-heading">{{ bp('heading') }}</heading>
      </template>
      <template slot="actions">
        <div class="buttons buttons--right-for-lg md:inline-flex lg:flex">
          <trek-link
            v-show="!showAllocatedBtn"
            button
            primary
            small
            qaid="manage-allocated-items-top-link"
            :href="`${encodedContextPath}/checkout/`"
          >
            <span v-html="bp('manageAllocatedItems')" />
          </trek-link>
        </div>
      </template>
    </section-header>
    <p class="mt-3" qaid="allocated-backorder-description">{{ bp('description') }}</p>
    <div v-show="!etaTab" class="lg:flex mt-6">
      <div class="lg:w-2/3">
        <allocated-backorder-history-filters
          ref="filters"
          :base-properties="baseProperties"
          :redirect-filter-sku="redirectFilterSku"
          @changed="onFilterChanged"
        />
      </div>
      <div class="hidden md:flex lg:justify-center md:mt-4 lg:mt-0 lg:w-1/3">
        <allocated-backorder-history-reports
          :base-properties="baseProperties"
          :hide-lines-without-comments="hideLinesWithoutComments"
          :hide-unallocated="hideUnallocated"
          :show-sold-out="showSoldOut"
          :show-project-one="showProjectOne"
          :description-search-term="descriptionSearchTerm"
          :sku-search-term="skuSearchTerm"
          :order-number-search-term="orderNumberSearchTerm"
          :customer-po-search-term="customerPoSearchTerm"
          :config-sort-by-for-url="configSortByForURL"
          :hide-preseason="hidePreseason"
          :current-tab="currentTab"
          :eta-tab="etaTab"
        />
      </div>
    </div>

    <div>
      <pdl-loading page :is-loading="loading">
        <!-- Orders Tabs -->
        <section class="my-5">
          <pdl-tabs ref="tabs" :value="currentTab" @input="tabChanged">
            <pdl-tab-pane
              name="bike"
              :label="`${bp('bikes')} (${bike.allocatedItemCount})`"
              qaid="allocated-backorder-bikes-tab"
            >
              <pdl-callout>
                <template #content>
                  <strong
                    :class="['font-bold', {['hidden']: hideUnallocated}]"
                    qaid="bike-backordered-item-count-label"
                    v-html="`${bp('backordered')}:`"
                  />
                  <span
                    :class="['ml-25', {['hidden']: hideUnallocated}]"
                    qaid="bike-backordered-item-count"
                    v-text="bike.backorderedItemCount"
                  />
                  <span
                    v-if="!consumerFriendlyMode && !hideUnallocated"
                    qaid="bike-backordered-item-price-total"
                    v-text="` (${bike.backorderedProductPriceTotal})`"
                  />
                  <span :class="['mx-1', {['hidden']: hideUnallocated}]" v-text="'|'" />
                  <strong qaid="bike-allocated-item-count-label" class="font-bold" v-html="`${bp('allocated')}:`" />
                  <span qaid="bike-allocated-item-count" class="ml-25" v-text="bike.allocatedItemCount" />
                  <span
                    v-if="!consumerFriendlyMode"
                    qaid="bike-allocated-item-price-total"
                    v-text="` (${bike.allocatedProductPriceTotal})`"
                  />
                </template>
              </pdl-callout>
              <allocated-backorder-history-grid
                :grid-data="bike"
                :columns="tableHeaders"
                :base-properties="baseProperties"
                :size="bike.pageSize"
                @update-sort-type="updateSortList"
                @update-data-list="fetchAllOrders"
              />
            </pdl-tab-pane>
            <pdl-tab-pane
              name="aftermarket"
              :label="`${bp('accessories')} (${aftermarket.allocatedItemCount})`"
              qaid="allocated-backorder-accessories-tab"
            >
              <pdl-callout>
                <template #content>
                  <strong
                    :class="['font-bold', {['hidden']: hideUnallocated}]"
                    qaid="aftermarket-backordered-item-count-label"
                    v-html="`${bp('backordered')}:`"
                  />
                  <span
                    :class="['ml-25', {['hidden']: hideUnallocated}]"
                    qaid="aftermarket-backordered-item-count"
                    v-text="aftermarket.backorderedItemCount"
                  />
                  <span
                    v-if="!consumerFriendlyMode && !hideUnallocated"
                    qaid="aftermarket-backordered-item-price-total"
                    v-text="` (${aftermarket.backorderedProductPriceTotal})`"
                  />
                  <span :class="['mx-1', {['hidden']: hideUnallocated}]" v-text="'|'" />
                  <strong
                    qaid="aftermarket-allocated-item-count-label"
                    class="font-bold"
                    v-html="`${bp('allocated')}:`"
                  />
                  <span qaid="aftermarket-allocated-item-count" class="ml-25" v-text="aftermarket.allocatedItemCount" />
                  <span
                    v-if="!consumerFriendlyMode"
                    qaid="aftermarket-allocated-item-price-total"
                    v-text="` (${aftermarket.allocatedProductPriceTotal})`"
                  />
                </template>
              </pdl-callout>
              <allocated-backorder-history-grid
                :grid-data="aftermarket"
                :columns="tableHeaders"
                :base-properties="baseProperties"
                :size="aftermarket.pageSize"
                @update-sort-type="updateSortList"
                @update-data-list="fetchAllOrders"
              />
            </pdl-tab-pane>
            <pdl-tab-pane name="bikeEta" :label="`${bp('bike-eta')}`" qaid="allocated-backorder-bikes-eta-tab">
              <allocated-backorder-history-eta-grid
                :items="EtaBikeData.orderLines || []"
                :token="token"
                :base-properties="baseProperties"
                :no-item="bikesEtaNoItemMessage"
                qaid="backorder-allocated-grid-eta-bike"
                @update-data-list="fetchAllOrders"
              />
            </pdl-tab-pane>
            <pdl-tab-pane
              name="aftermarketEta"
              :label="`${bp('acc-eta')}`"
              qaid="allocated-backorder-accessories-eta-tab"
            >
              <allocated-backorder-history-eta-grid
                :items="EtaAftermarketData.orderLines || []"
                :token="token"
                :base-properties="baseProperties"
                :no-item="partsEtaNoItemMessage"
                qaid="backorder-allocated-grid-eta-accessories"
                @update-data-list="fetchAllOrders"
              />
            </pdl-tab-pane>
          </pdl-tabs>
          <div class="flex md:hidden justify-center">
            <allocated-backorder-history-reports
              v-show="!etaTab"
              :base-properties="baseProperties"
              :hide-lines-without-comments="hideLinesWithoutComments"
              :hide-unallocated="hideUnallocated"
              :show-sold-out="showSoldOut"
              :show-project-one="showProjectOne"
              :description-search-term="descriptionSearchTerm"
              :sku-search-term="skuSearchTerm"
              :order-number-search-term="orderNumberSearchTerm"
              :customer-po-search-term="customerPoSearchTerm"
              :config-sort-by-for-url="configSortByForURL"
              :hide-preseason="hidePreseason"
              :current-tab="currentTab"
            />
          </div>
        </section>
      </pdl-loading>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {mapState, mapGetters} from 'vuex';
import {Url} from '@/utils/url';
import TrekLink from '@/components/TrekLink';
import SectionHeader from '@/components/SectionHeader';
import Heading from '@/components/Heading';
import {PdlTabs, PdlTabPane} from '@pedal/pdl-tabs';
import AllocatedBackorderHistoryGrid from '@/components/containers/my-account/AllocatedBackorderHistoryGrid';
import {PdlCallout} from '@pedal/pdl-callout';
import {PdlLoading} from '@pedal/pdl-loading';
import AllocatedBackorderHistoryFilters from '@/components/containers/my-account/AllocatedBackorderHistoryFilters';
import AllocatedBackorderHistoryReports from '@/components/containers/my-account/AllocatedBackorderHistoryReports';
import AllocatedBackorderHistoryEtaGrid from '@/components/containers/my-account/AllocatedBackorderHistoryEtaGrid';
import Vue from 'vue';

export default {
  components: {
    TrekLink,
    SectionHeader,
    Heading,
    PdlTabs,
    PdlTabPane,
    AllocatedBackorderHistoryGrid,
    PdlCallout,
    PdlLoading,
    AllocatedBackorderHistoryFilters,
    AllocatedBackorderHistoryReports,
    AllocatedBackorderHistoryEtaGrid,
  },

  props: {
    baseProperties: {
      type: Array,
      default: undefined,
    },
    tableHeaders: {
      type: Array,
      default: undefined,
    },
    redirectSku: {
      type: String,
      required: false,
      default: null,
    },
    redirectProductType: {
      type: String,
      required: false,
      default: null,
    },
    token: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      loading: true,
      hideUnallocated: false,
      hideLinesWithoutComments: false,
      showSoldOut: false,
      showProjectOne: false,
      hideWarranties: false,
      hidePreseason: false,
      descriptionSearchTerm: '',
      orderNumberSearchTerm: '',
      customerPoSearchTerm: '',
      skuSearchTerm: '',
      pageIndex: 0,
      pageSize: 50,
      bike: {
        type: 'bike',
        items: null,
        allocatedItemCount: 0,
        allocatedProductPriceTotal: 0,
        backorderedProductPriceTotal: 0,
        totalItemCount: 0,
        pageIndex: 0,
        pageSize: 50,
        pageCount: 0,
        noItemsAreShippable: false,
        sortKey: 'allocated',
        isFilterApplied: false,
      },
      aftermarket: {
        type: 'aftermarket',
        items: null,
        allocatedItemCount: 0,
        allocatedProductPriceTotal: 0,
        backorderedProductPriceTotal: 0,
        totalItemCount: 0,
        pageIndex: 0,
        pageSize: 50,
        pageCount: 0,
        noItemsAreShippable: false,
        sortKey: 'allocated',
        isFilterApplied: false,
      },
      bikeEta: {
        isFilterApplied: false,
      },
      aftermarketEta: {
        isFilterApplied: false,
      },
      currentTab: 'bike',
      redirectFilterSku: '',
      redirectFilterProductType: '',
      EtaBikeData: [],
      EtaAftermarketData: [],
      noItemDefaultMessage: 'Sorry, you have no watched items to display',
      bikesEtaNoItemMessage: '',
      partsEtaNoItemMessage: '',
    };
  },

  computed: {
    configSortByForURL() {
      return this.currentTab === 'bike' ? this.bike.sortKey : this.aftermarket.sortKey;
    },
    showAllocatedBtn() {
      return this.bike.noItemsAreShippable && this.aftermarket.noItemsAreShippable;
    },
    redirectProduct() {
      return this.redirectProductType === 'am' ? 'aftermarket' : 'bike';
    },
    etaTab() {
      return this.currentTab === 'bikeEta' || this.currentTab === 'aftermarketEta';
    },
    ...mapState('backend', ['encodedContextPath']),
    ...mapGetters('backend', ['consumerFriendlyMode']),
  },
  watch: {
    etaTab() {
      this.fetchAllOrders();
    },
  },

  created() {
    this.applyRedirectDataToFilterData();
    this.fetchAllOrders();
    this.currentTab = this.redirectProduct ? this.redirectProduct : 'bike';
    this.removeRedirectParametersFromUrl();
  },

  provide() {
    return {
      container: this,
    };
  },
  methods: {
    updateSortList(sortKey) {
      if (this.currentTab === 'bike') {
        this.bike.sortKey = sortKey;
        this.fetchOrders('bike', sortKey);
      } else {
        this.aftermarket.sortKey = sortKey;
        this.fetchOrders('aftermarket', sortKey);
      }
    },
    fetchAllOrders() {
      this.fetchOrders('bike');
      this.fetchOrders('aftermarket');
      this.fetchEtaBikeData();
      this.fetchEtaAftermarketData();
    },

    fetchOrders(type = 'bike', sortKey = null) {
      this.loading = true;
      sortKey = sortKey ?? this.configSortByForURL ?? 'allocated';
      const payload = {
        type: type,
        hideUnallocated: this.hideUnallocated,
        hideLinesWithoutComments: this.hideLinesWithoutComments,
        showSoldOut: this.showSoldOut,
        showProjectOne: this.showProjectOne,
        hideWarranties: this.hideWarranties,
        hidePreseason: this.hidePreseason,
        pageIndex: this[type].pageIndex,
        pageSize: this[type].pageSize,
        sortOrder: sortKey === 'allocated' ? 'desc' : 'asc',
        sortBy: sortKey,
      };

      if (this.descriptionSearchTerm.length > 0) {
        payload.descriptionSearchTerm = this.descriptionSearchTerm;
      }

      if (this.orderNumberSearchTerm.length > 0) {
        payload.orderNumberSearchTerm = this.orderNumberSearchTerm;
      }

      if (this.customerPoSearchTerm.length > 0) {
        payload.customerPoSearchTerm = this.customerPoSearchTerm;
      }

      if (this.skuSearchTerm.length > 0 && !this.redirectFilterProductType) {
        payload.skuSearchTerm = this.skuSearchTerm;
      }

      if (this.skuSearchTerm.length > 0 && this.redirectFilterProductType) {
        if (this.redirectProduct === type) payload.skuSearchTerm = this.redirectFilterSku;
      }

      axios
        .post(`/allocated/items`, payload)
        .then((response) => {
          if (
            response.data.meta.feedback.type === 'success' &&
            response.data.data &&
            Object.keys(response.data.data).length > 0
          ) {
            let data = response.data.data;

            this[type] = {
              type: type,
              items: type === 'bike' ? data.bikeVariantProducts : data.aftermarketVariantProducts,
              archiveEnabled: type === 'bike' ? data.bikeArchiveEnabled : data.aftermarketArchiveEnabled,
              allocatedItemCount:
                type === 'bike'
                  ? data.bikeVariantAllocatedProductsQtyTotal
                  : data.aftermarketVariantAllocatedProductsQtyTotal,
              allocatedProductPriceTotal:
                type === 'bike'
                  ? data.bikeVariantAllocatedProductsPriceTotal
                  : data.aftermarketVariantAllocatedProductsPriceTotal,
              backorderedProductPriceTotal:
                type === 'bike'
                  ? data.bikeVariantBackorderedProductsPriceTotal
                  : data.aftermarketVariantBackorderedProductsPriceTotal,
              backorderedItemCount:
                type === 'bike'
                  ? data.bikeVariantBackorderedProductsQtyTotal
                  : data.aftermarketVariantBackorderedProductsQtyTotal,
              totalItemCount: type === 'bike' ? data.bikeVariantProductsTotal : data.aftermarketVariantProductsTotal,
              pageIndex: this[type].pageIndex,
              pageSize: this[type].pageSize,
              isFilterApplied: this[type].isFilterApplied,
              noItemsAreShippable: data.noItemsAreShippable,
              sortKey,
            };

            if (
              this[type].totalItemCount > 0 &&
              this[type].pageSize > 0 &&
              this[type].totalItemCount / this[type].pageSize > 1
            ) {
              this[type].pageCount = Math.ceil(this[type].totalItemCount / this[type].pageSize);
            } else {
              this[type].pageCount = 0;
            }

            if (this[type].pageIndex > this[type].pageCount) {
              this[type].pageIndex = 0;
              this.fetchOrders(type);
            }
          } else {
            this[type] = {
              type: type,
              items: [],
              allocatedItemCount: 0,
              allocatedProductPriceTotal: 0,
              backorderedItemCount: 0,
              backorderedProductPriceTotal: 0,
              totalItemCount: 0,
              pageIndex: 0,
              pageSize: this[type].pageSize,
              isFilterApplied: this[type].isFilterApplied,
              sortKey,
            };
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onFilterChanged(value) {
      this.hideUnallocated = value.hideUnallocated;
      this.hideLinesWithoutComments = value.hideLinesWithoutComments;
      this.showSoldOut = value.showSoldOut;
      this.showProjectOne = value.showProjectOne;
      this.descriptionSearchTerm = value.description ? value.description : '';
      this.orderNumberSearchTerm = value.orderNumber ? value.orderNumber : '';
      this.customerPoSearchTerm = value.customerPo ? value.customerPo : '';
      this.skuSearchTerm = value.sku;
      this.redirectFilterProductType = '';
      this.redirectFilterSku = '';

      if (this[this.currentTab].isFilterApplied || !this.isEmptyFilter()) {
        this[this.currentTab].isFilterApplied = !this.isEmptyFilter();
        this.fetchOrders(this.currentTab);
      }
    },

    isEmptyFilter() {
      return (
        !this.hideUnallocated &&
        !this.hideLinesWithoutComments &&
        !this.showSoldOut &&
        !this.showProjectOne &&
        !this.descriptionSearchTerm.length &&
        !this.orderNumberSearchTerm.length &&
        !this.customerPoSearchTerm.length &&
        !this.skuSearchTerm.length &&
        !this.redirectFilterSku.length &&
        !this.redirectFilterProductType.length
      );
    },

    resetFilters() {
      this.hideUnallocated = false;
      this.hideLinesWithoutComments = false;
      this.showSoldOut = false;
      this.showProjectOne = false;
      this.descriptionSearchTerm = '';
      this.orderNumberSearchTerm = '';
      this.customerPoSearchTerm = '';
      this.skuSearchTerm = '';
      this.redirectFilterSku = '';
      this.redirectFilterProductType = '';
      this.$refs.filters.resetFilters();

      if (this[this.currentTab].isFilterApplied) {
        this.fetchOrders(this.currentTab);
        this[this.currentTab].isFilterApplied = false;
      }
    },

    tabChanged(paneName) {
      if (!this.etaTab) {
        this.resetFilters();
      }
      this.currentTab = paneName;
    },

    applyRedirectDataToFilterData() {
      this.redirectFilterSku = this.redirectSku ? this.redirectSku : this.skuSearchTerm;
      this.skuSearchTerm = this.redirectFilterSku ? this.redirectFilterSku : this.skuSearchTerm;
      this.redirectFilterProductType = this.redirectProductType
        ? this.redirectProductType
        : this.redirectFilterProductType;
      if (this.redirectFilterSku) {
        this[this.redirectProduct].isFilterApplied = true;
      }
    },
    removeRedirectParametersFromUrl() {
      window.history.replaceState(
        null,
        document.title,
        Url.update(window.location.href, undefined, {
          removeKeys: ['productType', 'skuSearchTerm'],
        })
      );
    },
    fetchEtaBikeData() {
      return axios.get('/notification-settings/eta-watch?producttype=bike').then(({data: {data, meta}}) => {
        data.data = [];
        if (data.orderLines) {
          data.orderLines.forEach((element) => {
            Vue.set(element, 'checkboxActive', false);
            Vue.set(element, 'etaWatch', true);
          });
        }
        this.bikesEtaNoItemMessage =
          meta.feedback.message && meta.feedback.message != null
            ? meta.feedback.message.basePropertyValue
            : this.noItemDefaultMessage;
        this.EtaBikeData = data;
      });
    },
    fetchEtaAftermarketData() {
      return axios.get('/notification-settings/eta-watch?producttype=am').then(({data: {data, meta}}) => {
        if (data.orderLines) {
          data.orderLines.forEach((element) => {
            Vue.set(element, 'checkboxActive', false);
            Vue.set(element, 'etaWatch', true);
          });
        }
        this.partsEtaNoItemMessage =
          meta.feedback.message && meta.feedback.message != null
            ? meta.feedback.message.basePropertyValue
            : this.noItemDefaultMessage;
        this.EtaAftermarketData = data;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.page-myAccountBackordersAllocatedB2B .account-section-content .grid-container {
  max-width: 100%;
  margin: 0 auto;
}

// Ensure that style is passed down into child component via /deep/, otherwise it only impacts parent
/deep/ .pdl-tabs .pdl-tabs__item {
  @apply font-heading;

  @include breakpoint(medium) {
    @apply text-xl;
  }

  @apply m-0;
}
</style>
